import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import type {FocusEvent, FormEvent} from 'inferno';

import {CustomComponentProps, CustomComponentState, CustomComponent} from '../components';
import {KEY} from '../constants';
import {Icon} from './Icon';


export interface BaseSearchUIValue {
    key(): string;
    toUserString(): string;
}

export interface BaseSearchUIProps extends CustomComponentProps {
}

export interface BaseSearchUIState<
    T> extends CustomComponentState {
    keyPressed: boolean;
    selectedSuggestionIndex: number;
    suggestions: T[];
    focused: boolean;
    value: string;
}

export abstract class BaseSearchUI<
    T extends BaseSearchUIValue,
    ChildProps extends BaseSearchUIProps,
    ChildState extends BaseSearchUIState<T>>
    extends CustomComponent<ChildProps, ChildState> {
    static debugNamespace = 'BaseSearchUI';

    maxSuggestions: number;
    input: HTMLInputElement|null;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        this.maxSuggestions = 6;
        this.input = null;
        this.state = {
            ...this.state,
            keyPressed: false,
            selectedSuggestionIndex: -1,
            suggestions: [],
            focused: false,
            value: '',
        };
    }
    valueToString(val: T): string {
        return val.key();
    }
    abstract stringToValue(s: string): T
    @autobind
    placeholder() {
        const gettextSub = this.context.gtService.gettextSub;

        // Translators: This is a text input placeholder.
        return gettextSub('Search');
    }
    fetchSuggestions(reqValue: string): Promise<T[]> {
        return Promise.resolve([]);
    }
    setSuggestions(suggestions: T[]) {
        this.setState({
            suggestions,
        });
        this.clearSuggestionSelection();
    }
    @autobind
    clearSuggestionSelection() {
        this.setState({
            selectedSuggestionIndex: -1,
        });
    }
    selectSuggestionByOffset(offset: number) {
        let {
            suggestions,
            selectedSuggestionIndex,
        } = this.state;
        const numSuggestions = Math.min(
            suggestions.length, this.maxSuggestions);
        if (numSuggestions) {
            selectedSuggestionIndex += offset;
            if (selectedSuggestionIndex >= numSuggestions) {
                selectedSuggestionIndex = -1;
            } else if (selectedSuggestionIndex < -1) {
                selectedSuggestionIndex = numSuggestions - 1;
            }
        }
        this.setState({
            selectedSuggestionIndex,
        });
    }
    @autobind
    handleOnKeyDown(event: KeyboardEvent) {
        this.setState({
            keyPressed: true,
        });
        switch (event.keyCode) {
        case KEY.DOWN:
            event.preventDefault();
            this.selectSuggestionByOffset(1);
            break;
        case KEY.UP:
            event.preventDefault();
            this.selectSuggestionByOffset(-1);
            break;
        case KEY.ESC:
            event.preventDefault();
            this.clearSuggestionSelection();
            break;
        }
    }
    @autobind
    setInputElement(element: HTMLInputElement|null) {
        this.input = element;
    }
    @autobind
    focus() {
        this.input?.focus();
    }
    abstract renderSuggestionDropDown()
}

export interface BaseSearchBarProps extends BaseSearchUIProps{
}

export interface BaseSearchBarState<
    T> extends BaseSearchUIState<T>{
}


export abstract class BaseSearchBar<
    T extends BaseSearchUIValue
    > extends BaseSearchUI<T, BaseSearchBarProps, BaseSearchBarState<T>> {
    static debugNamespace = 'BaseSearchBar';

    cleanInput(s: string): string {
        return s.toLowerCase()
            .replace(/"/g, '').replace(/[\s,]+/g, ' ').replace(/^\s+/, '');
    }
    @autobind
    handleOnInput(event: FormEvent<HTMLInputElement>) {
        const s = this.cleanInput((event.target as HTMLInputElement).value);
        this.setStateSynchronously({value: s});
        this.triggerUpdate();
        const reqValue = s.trim();
        this.fetchSuggestions(reqValue).then(this.triggerUpdate);

        // Putting this in a setTimeout in needed for IE11 compatibility.
        // setTimeout(function(){
        //     that.triggerUpdate();
        // }, 0);
    }
    @autobind
    handleOnBlur(event: FocusEvent<HTMLInputElement>) {
        this.setStateSynchronously({focused: false});
        setTimeout(() => {
            this.triggerUpdate();
        }, 500);
    }
    @autobind
    handleOnFocus(event: FocusEvent<HTMLInputElement>) {
        this.setStateSynchronously({focused: true});
        this.triggerUpdate();
    }
    @autobind
    handleIconMouseDown(event: FormEvent<HTMLInputElement>) {
        if (this.state.focused) {
            // perform search
            this.handleSubmit();
        } else {
            this.focus();
        }
    }
    abstract handleSubmit(event?: Event): void
    render() {
        const shouldRenderSuggestionsDropdown = this.state.focused &&
            this.state.keyPressed && this.state.suggestions.length > 0;

        return (<form className="SearchBar" onSubmit={this.handleSubmit}>
            <input
                ref={this.setInputElement}
                onInput={this.handleOnInput}
                onBlur={this.handleOnBlur}
                onFocus={this.handleOnFocus}
                onKeyDown={this.handleOnKeyDown}
                name="q"
                value={this.state.value}
                placeholder={this.placeholder()}
                autoComplete="off"
            />
            <Icon name="search" onMouseDown={this.handleIconMouseDown}/>
            { shouldRenderSuggestionsDropdown &&
                this.renderSuggestionDropDown() }
        </form>);
    }
}
